import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  CheckCircle2, Phone, MapPin, ChevronRight, ShieldCheck, Clock,
  Wrench, RefreshCw, Calculator as CalcIcon
} from "lucide-react";
import { getLocation, LOCATIONS, COUNTIES } from "../data/locations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_tank-replace-pro/artifacts/isbxf87e_Logo%20Kool%20Plumbing-01.png";
const SITE_URL = "https://koolplumbing.com";

const LocationServicePage = ({ addToast }) => {
  const { slug } = useParams();
  const loc = getLocation(slug);

  const [formData, setFormData] = useState({ name: "", email: "", phone: "", zipCode: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!loc) return <Navigate to="/service-areas" replace />;

  const county = COUNTIES[loc.countyKey];
  const pageTitle = `Plumbing & Septic Services in ${loc.city}, FL | Kool Plumbing`;
  const pageDesc = `Licensed plumber & septic tank replacement specialist serving ${loc.city}, ${county.name}. Free quotes, fast permitting, 24/7 emergency service. License #CFC1432999. Call (786) 622-4963.`;
  const canonical = `${SITE_URL}/service-areas/${loc.slug}`;

  const faqs = [
    { q: `Do you offer plumbing service in ${loc.shortCity}?`, a: `Yes — Kool Plumbing provides full residential plumbing and septic tank replacement throughout ${loc.city} and the surrounding ${county.name} area, including repairs, repipes, water heaters, drain cleaning, and emergency service.` },
    { q: `How much does septic tank replacement cost in ${loc.shortCity}?`, a: `Most residential septic replacements range from about $6,500 to $18,000 depending on tank size, soil, drain field work, and permits. Use our free online estimator for an instant range, then we confirm with a free on-site quote.` },
    { q: `Do you handle permits in ${county.name}?`, a: `Absolutely. We manage soil testing, engineering, and all ${county.name} permitting in-house so you never deal with paperwork.` },
    { q: `Are you licensed and insured?`, a: `Yes. Kool Plumbing is a Florida State Certified Plumbing Contractor (License #CFC1432999), fully licensed and insured across Miami-Dade and Broward.` },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "Service", serviceType: "Plumbing & Septic Tank Replacement", provider: { "@type": "Plumber", name: "Kool Plumbing", telephone: "+1-786-622-4963", url: SITE_URL }, areaServed: { "@type": "City", name: `${loc.city}, FL` }, name: `Plumbing & Septic Services in ${loc.city}`, description: pageDesc, url: canonical },
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE_URL}/service-areas` },
        { "@type": "ListItem", position: 3, name: loc.city, item: canonical },
      ] },
      { "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    ],
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccess(false);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        zip_code: formData.zipCode, service_type: "septic_replacement",
        message: `[${loc.city} page] ${formData.message}`,
      });
      setSuccess(true);
      addToast && addToast("Quote request sent! Our specialist will call you shortly.", "success");
      setFormData({ name: "", email: "", phone: "", zipCode: "", message: "" });
    } catch (err) {
      addToast && addToast("Failed to submit request. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sameCounty = LOCATIONS.filter((l) => l.countyKey === loc.countyKey && l.slug !== loc.slug).slice(0, 11);

  return (
    <div className="flex-1 font-body bg-white" data-testid={`location-page-${loc.slug}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="relative py-20 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1759355787118-7b48305965d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')" }}>
        <div className="absolute inset-0 bg-slate-900/75"></div>
        <div className="relative max-w-5xl mx-auto px-6 text-white space-y-6">
          <nav className="text-xs text-blue-200 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight size={12} />
            <Link to="/service-areas" className="hover:text-amber-400">Service Areas</Link>
            <ChevronRight size={12} />
            <span className="text-amber-400">{loc.city}</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-600/30 border border-amber-600/50 text-amber-300 text-sm font-semibold">
            <MapPin size={16} /> Serving {county.name}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading leading-tight tracking-tight max-w-3xl">
            Plumbing &amp; Septic Services in {loc.city}, FL
          </h1>
          <p className="text-lg text-gray-200 max-w-2xl leading-relaxed">{loc.blurb}</p>
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <a href="#quote" data-testid="location-cta-quote" className="px-8 py-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold tracking-wide shadow-lg transition-all transform hover:-translate-y-0.5 text-center">Get a Free Quote</a>
            <a href="tel:+17866224963" className="px-8 py-4 rounded-xl bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold backdrop-blur transition-all flex items-center justify-center gap-2"><Phone size={18} /> (786) 622-4963</a>
          </div>
        </div>
      </section>

      <section className="bg-blue-950 py-6">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
          {[["Licensed", "#CFC1432999"], ["Free Quote", "On-Site Inspections"], ["24/7 Care", "Emergency Service"], ["15+ Years", "Local Experience"]].map(([t, s]) => (
            <div key={t} className="space-y-1">
              <span className="text-amber-500 font-heading text-lg font-bold block">{t}</span>
              <span className="text-xs text-gray-400">{s}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            <div className="space-y-4">
              <h2 className="text-3xl font-heading text-blue-900">Your Local {loc.shortCity} Plumbing &amp; Septic Team</h2>
              <p className="text-gray-600 leading-relaxed">{county.soilNote}</p>
            </div>

            <div className="space-y-4">
              <h3 className="text-2xl font-heading text-blue-950">Services we offer in {loc.shortCity}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {["Septic tank replacement & installation", "Drain field repair & reclamation", "Whole-home repiping", "Water heater installation & repair", "Leak detection & slab leaks", "Drain cleaning & sewer camera", "Fixture & faucet installation", "24/7 emergency plumbing"].map((s) => (
                  <div key={s} className="flex items-start gap-3 text-sm text-gray-600">
                    <span className="text-emerald-500 mt-0.5"><CheckCircle2 size={18} /></span><span>{s}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-2xl font-heading text-blue-950">Neighborhoods we serve in {loc.shortCity}</h3>
              <div className="flex flex-wrap gap-2">
                {loc.neighborhoods.map((n) => (
                  <span key={n} className="px-3 py-1.5 bg-blue-50 text-blue-900 rounded-full text-xs font-semibold border border-blue-100">{n}</span>
                ))}
              </div>
              <p className="text-xs text-gray-400">Service ZIP codes include: {loc.zips.join(", ")} and surrounding areas.</p>
            </div>

            <div className="space-y-4">
              <h3 className="text-2xl font-heading text-blue-950">How we work</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {[[CalcIcon, "1. Free Estimate", "Use our online estimator or book a free on-site inspection."], [ShieldCheck, "2. Permits & Testing", "We handle soil testing and all county permits."], [Wrench, "3. Expert Install", "Premium parts installed to code by certified techs."], [Clock, "4. Done Right", "Clean job site, restoration, and final sign-off."]].map(([Icon, t, d]) => (
                  <div key={t} className="p-5 bg-gray-50 rounded-2xl border border-gray-100 space-y-2">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-900 flex items-center justify-center"><Icon size={20} /></div>
                    <h4 className="font-heading text-blue-950 text-base">{t}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-2xl font-heading text-blue-950">Frequently asked questions</h3>
              <div className="space-y-3">
                {faqs.map((f) => (
                  <details key={f.q} className="group bg-gray-50 rounded-xl border border-gray-100 p-5">
                    <summary className="font-semibold text-blue-950 text-sm cursor-pointer list-none flex justify-between items-center">{f.q}<ChevronRight size={16} className="group-open:rotate-90 transition-transform text-amber-600" /></summary>
                    <p className="text-sm text-gray-600 mt-3 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div id="quote" className="sticky top-24 bg-white border border-gray-200 rounded-2xl shadow-lg p-6 space-y-4">
              <h3 className="text-xl font-heading text-blue-950">Request a Free Quote</h3>
              <p className="text-xs text-gray-500">Service in {loc.shortCity}. We respond within 2 business hours.</p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input data-testid="location-lead-name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Full Name *" className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <input data-testid="location-lead-email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Email *" className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <input data-testid="location-lead-phone" type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="Phone *" className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <input data-testid="location-lead-zip" required value={formData.zipCode} onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })} placeholder="Property Zip Code *" className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <textarea data-testid="location-lead-message" rows={3} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Briefly describe your project" className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <button data-testid="location-lead-submit" type="submit" disabled={isSubmitting} className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-xl shadow transition disabled:bg-gray-200 flex items-center justify-center gap-2">{isSubmitting ? <RefreshCw className="animate-spin" size={18} /> : "Submit Free Quote Request"}</button>
              </form>
              {success && (<div data-testid="location-lead-success" className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex gap-2 items-center"><CheckCircle2 size={16} /> <span>Success! We'll call you shortly.</span></div>)}
              <Link to="/#calculator" className="block text-center text-xs text-blue-700 hover:underline pt-1">Try our instant cost estimator →</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6 space-y-6">
          <h3 className="text-xl font-heading text-blue-950">More {county.name} areas we serve</h3>
          <div className="flex flex-wrap gap-3">
            {sameCounty.map((l) => (
              <Link key={l.slug} to={`/service-areas/${l.slug}`} className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-blue-900 hover:bg-blue-50 hover:border-blue-200 transition">{l.city}</Link>
            ))}
            <Link to="/service-areas" className="px-4 py-2 bg-blue-900 text-white rounded-xl text-sm font-semibold hover:bg-blue-800 transition">View all areas →</Link>
          </div>
        </div>
      </section>

      <footer className="bg-blue-950 text-white py-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Kool Plumbing" className="h-12 w-auto" />
          <div className="text-xs text-gray-300 text-center sm:text-right space-y-1">
            <p className="flex items-center gap-2 justify-center sm:justify-end"><Phone size={13} className="text-amber-500" /> (786) 622-4963 · info@koolplumbing.com</p>
            <p>Serving Miami-Dade &amp; Broward · License #CFC1432999</p>
            <Link to="/" className="text-amber-500 hover:underline">← Back to homepage</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LocationServicePage;
